import { SL_WEB_CAM_PAYWALL_REDESIGN } from 'common/treatments';
import config from 'config';
import { useRouter } from 'next/router';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSpotReportAssociated } from 'selectors/spot';
import { useUserPermissionStatus } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import { floorToMidnightForTZ, oneDay } from 'utils/time';
import { useTreatments } from 'utils/treatments';
import { RewindCamPlayerHandle } from 'components/RewindCamPlayer/RewindCamPlayer';
import { RewindPlaylists } from 'utils/rewinds/playlistBlocks.types';
import { LiveCamPlayerHandle } from '../CamPlayerKbygRewindsTypes';

export interface PlayerState {
  visible: Player;
  committed: Player;
}

export enum Player {
  LIVE = 'LIVE',
  REWIND = 'REWIND',
  HIGHLIGHTS = 'HIGHLIGHTS',
}

interface UsePlayerStateProps {
  adsEnabled: boolean;
  liveCamPlayerHandle: RefObject<LiveCamPlayerHandle>;
  rewindCamPlayerHandle: RefObject<RewindCamPlayerHandle>;
  isNonPremiumPlaylist: boolean;
  hasCamRewindPermissions: boolean;
  setUserWantsRewindPlaying: (wantsRewindPlaying: boolean) => void;
  multiCamViewActive: boolean;
  rewindsLoading: boolean;
  rewindsPlaylists?: RewindPlaylists;
}

export const usePlayerState = ({
  adsEnabled,
  liveCamPlayerHandle,
  rewindCamPlayerHandle,
  isNonPremiumPlaylist,
  hasCamRewindPermissions,
  setUserWantsRewindPlaying,
  multiCamViewActive,
  rewindsLoading,
  rewindsPlaylists,
}: UsePlayerStateProps) => {
  const { query } = useRouter();
  const timezone = useAppSelector(getSpotReportAssociated)?.timezone || 'America/Los_Angeles';
  const { pageSection, currentTimestamp: queryTimestamp } = query;
  const { hasAdFreeCamPermissions, hasAdSupportedCamPermissions } = useUserPermissionStatus();
  const treatments = useTreatments();

  const isCamPaywallRedesignEnabled = treatments?.[SL_WEB_CAM_PAYWALL_REDESIGN] === 'on';

  const fiveDaysAgoMidnight = useMemo(
    () =>
      floorToMidnightForTZ(
        new Date().getTime() - oneDay * (config.numberOfRewindDays - 1),
        timezone,
      ),
    [timezone],
  );

  const isQueryTimestampValid = queryTimestamp && Number(queryTimestamp) >= fiveDaysAgoMidnight;

  const useRedesignedPaywall =
    !hasAdFreeCamPermissions &&
    !hasAdSupportedCamPermissions &&
    adsEnabled &&
    isCamPaywallRedesignEnabled;

  const sharingLinkDefaultState =
    pageSection === 'rewind' && isQueryTimestampValid ? Player.REWIND : Player.LIVE;

  const paywallRedesignDefaultState = useRedesignedPaywall ? Player.REWIND : Player.LIVE;

  const initialState =
    pageSection === 'rewind' || pageSection === 'liveCam'
      ? sharingLinkDefaultState
      : paywallRedesignDefaultState;

  const [playerState, setPlayerState] = useState<PlayerState>({
    visible: initialState,
    committed: initialState,
  });

  const setPreviewPlayer = useCallback((player: Player) => {
    setPlayerState((previousState) => ({
      ...previousState,
      visible: player,
    }));
  }, []);

  const currentPlayerRef = useRef(playerState.committed);

  const setCurrentPlayer = useCallback((player: Player) => {
    setPlayerState({
      visible: player,
      committed: player,
    });
    currentPlayerRef.current = player;
  }, []);

  const currentPlayer = playerState.committed;
  const currentPreviewPlayer = playerState.visible;

  const setPlayerRewind = useCallback(() => {
    if (
      currentPlayerRef.current === Player.LIVE &&
      (isNonPremiumPlaylist || hasCamRewindPermissions)
    ) {
      setUserWantsRewindPlaying(true);
    }

    setCurrentPlayer(Player.REWIND);
    liveCamPlayerHandle.current?.pause();
    liveCamPlayerHandle.current?.exitNativePictureInPicture();
  }, [
    currentPlayerRef,
    setCurrentPlayer,
    setUserWantsRewindPlaying,
    isNonPremiumPlaylist,
    hasCamRewindPermissions,
    liveCamPlayerHandle,
  ]);

  const setPlayerLive = useCallback(
    (resumePlayback = true) => {
      rewindCamPlayerHandle.current?.pause();
      rewindCamPlayerHandle.current?.exitNativePictureInPicture();
      setUserWantsRewindPlaying(false);
      setCurrentPlayer(Player.LIVE);
      if (resumePlayback) {
        liveCamPlayerHandle.current?.play();
      }
    },
    [setCurrentPlayer, rewindCamPlayerHandle, setUserWantsRewindPlaying, liveCamPlayerHandle],
  );

  const previewPlayerRewind = useCallback(() => {
    liveCamPlayerHandle.current?.pause();
    setPreviewPlayer(Player.REWIND);
  }, [setPreviewPlayer, liveCamPlayerHandle]);

  const previewPlayerLive = useCallback(() => {
    setPreviewPlayer(Player.LIVE);
  }, [setPreviewPlayer]);

  useEffect(() => {
    if (multiCamViewActive) {
      setPlayerLive(false);
    }
  }, [multiCamViewActive, setPlayerLive]);

  useEffect(() => {
    if (
      query.pageSection !== 'rewind' &&
      !isNonPremiumPlaylist &&
      !rewindsLoading &&
      !hasAdFreeCamPermissions &&
      !hasAdSupportedCamPermissions &&
      isCamPaywallRedesignEnabled
    ) {
      setPlayerLive(false);
    }
  }, [
    setPlayerLive,
    query.pageSection,
    hasAdFreeCamPermissions,
    hasAdSupportedCamPermissions,
    isNonPremiumPlaylist,
    rewindsLoading,
    isCamPaywallRedesignEnabled,
  ]);

  useEffect(() => {
    if (!rewindsLoading && !rewindsPlaylists?.size) {
      setCurrentPlayer(Player.LIVE);
    }
  }, [rewindsLoading, rewindsPlaylists, setCurrentPlayer]);

  return {
    currentPlayer,
    currentPlayerRef,
    currentPreviewPlayer,
    setPreviewPlayer,
    setCurrentPlayer,
    setPlayerLive,
    setPlayerRewind,
    previewPlayerLive,
    previewPlayerRewind,
  };
};
